import { resolveSalesChatConfig } from './sales-chat'

function bindCollectiveTabs(root: ParentNode = document): void {
  const shell = root.querySelector<HTMLElement>('[data-aes-tabs]')
  if (!shell) return

  const tabs = [...shell.querySelectorAll<HTMLButtonElement>('[data-aes-tab]')]
  const panels = [...shell.querySelectorAll<HTMLElement>('[data-aes-panel]')]
  if (!tabs.length || !panels.length) return

  const select = (id: string, focusTab = false): void => {
    for (const tab of tabs) {
      const active = tab.dataset.aesTab === id
      tab.setAttribute('aria-selected', active ? 'true' : 'false')
      tab.tabIndex = active ? 0 : -1
      tab.classList.toggle('is-active', active)
      if (active && focusTab) tab.focus()
    }
    for (const panel of panels) {
      const active = panel.dataset.aesPanel === id
      panel.hidden = !active
    }
    const frame = root.querySelector<HTMLElement>('[data-aas-view-panel]')
    if (frame) frame.dataset.mode = id
  }

  for (const tab of tabs) {
    tab.addEventListener('click', () => {
      const id = tab.dataset.aesTab
      if (id) select(id)
    })
    tab.addEventListener('keydown', (event) => {
      const index = tabs.indexOf(tab)
      if (index < 0) return
      let next = -1
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length
      if (event.key === 'Home') next = 0
      if (event.key === 'End') next = tabs.length - 1
      if (next < 0) return
      event.preventDefault()
      const id = tabs[next]?.dataset.aesTab
      if (id) select(id, true)
    })
  }
}

type Side = { price: string; model: string }
type Task = { frontier: Side; routed: Side; note: string }

/* Prices from src/catalog.js (captured 2026-09-24), USD per 1M tokens in / out.
   Claude Opus 5.5 is 4 / 20. GPT-6 Luna is 0.10 / 0.50 (40x on both sides).
   Gemini 3.8 Flash is 0.75 / 3.75 (about 5x, not 40x).
   Kimi K2.7 Code is 0.6562 / 3.3 (about 6x, not 40x). */
const ROUTING: Record<string, Task> = {
  'sort-tickets': {
    frontier: { price: '$4 / $20', model: 'Claude Opus 5.5' },
    routed: { price: '$0.10 / $0.50', model: 'GPT-6 Luna' },
    note: 'Sorting 40 support tickets costs 40× less when it is routed to the right model. Same result, and the frontier stays available for the work that needs it.',
  },
  'summarize-churn': {
    frontier: { price: '$4 / $20', model: 'Claude Opus 5.5' },
    routed: { price: '$0.10 / $0.50', model: 'GPT-6 Luna' },
    note: 'Summarizing churn calls costs 40× less on GPT-6 Luna versus Claude Opus 5.5. Same shape of result, and the frontier stays available for the work that needs it.',
  },
  'translate-help': {
    frontier: { price: '$4 / $20', model: 'Claude Opus 5.5' },
    routed: { price: '$0.10 / $0.50', model: 'GPT-6 Luna' },
    note: 'Translating the help center costs 40× less on GPT-6 Luna versus Claude Opus 5.5. List prices, not a bill.',
  },
  'scan-replies': {
    frontier: { price: '$4 / $20', model: 'Claude Opus 5.5' },
    routed: { price: '$0.10 / $0.50', model: 'GPT-6 Luna' },
    note: 'Scanning launch replies costs 40× less on GPT-6 Luna versus Claude Opus 5.5.',
  },
  'read-contract': {
    frontier: { price: '$4 / $20', model: 'Claude Opus 5.5' },
    routed: { price: '$0.75 / $3.75', model: 'Gemini 3.8 Flash' },
    note: 'Reading a long contract on Gemini 3.8 Flash is about 5× less than Claude Opus 5.5 at published list prices ($0.75 / $3.75 versus $4 / $20). Not 40×.',
  },
  'refactor-billing': {
    frontier: { price: '$4 / $20', model: 'Claude Opus 5.5' },
    routed: { price: '$0.6562 / $3.30', model: 'Kimi K2.7 Code' },
    note: 'Refactoring the billing service on Kimi K2.7 Code is about 6× less than Claude Opus 5.5 at published list prices ($0.6562 / $3.30 versus $4 / $20). Not 40×.',
  },
}

function applyRouting(root: ParentNode, id: string): void {
  const task = ROUTING[id] ?? ROUTING['sort-tickets']
  root.querySelectorAll<HTMLButtonElement>('[data-aas-task]').forEach((btn) => {
    const active = btn.dataset.aasTask === id
    btn.setAttribute('aria-pressed', active ? 'true' : 'false')
    btn.classList.toggle('is-active', active)
  })
  const set = (sel: string, value: string) => {
    const node = root.querySelector(sel)
    if (node) node.textContent = value
  }
  set('[data-aas-frontier-price]', task.frontier.price)
  set('[data-aas-frontier-model]', task.frontier.model)
  set('[data-aas-routed-price]', task.routed.price)
  set('[data-aas-routed-model]', task.routed.model)
  set('[data-aas-routing-note]', task.note)
}

function applyStory(root: ParentNode, index: number): void {
  const cards = [...root.querySelectorAll<HTMLElement>('[data-aas-story]')]
  const dots = [...root.querySelectorAll<HTMLButtonElement>('[data-aas-dot]')]
  cards.forEach((card, i) => card.classList.toggle('is-active', i === index))
  dots.forEach((dot, i) => {
    const active = i === index
    dot.classList.toggle('is-active', active)
    dot.setAttribute('aria-current', active ? 'true' : 'false')
  })
  const rail = root.querySelector<HTMLElement>('[data-aas-rail]')
  const card = cards[index]
  if (rail && card && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    card.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  } else if (rail && card) {
    card.scrollIntoView({ inline: 'center', block: 'nearest' })
  }
}

function applySize(root: ParentNode, size: string): void {
  root.querySelectorAll<HTMLButtonElement>('[data-aas-size]').forEach((btn) => {
    const active = btn.dataset.aasSize === size
    btn.setAttribute('aria-pressed', active ? 'true' : 'false')
    btn.classList.toggle('is-active', active)
  })
  const status = root.querySelector('[data-aas-assessment-status]')
  if (status) {
    status.textContent = size
      ? `Team size ${size} selected. The assessment stays illustrative until you book a call.`
      : 'Choose a team size to begin the illustrative assessment.'
  }
}

function initNav(root: ParentNode): void {
  const nav = root.querySelector<HTMLElement>('[data-aas-nav]')
  const toggle = nav?.querySelector<HTMLButtonElement>('[data-aas-nav-toggle]')
  if (!nav || !toggle) return
  const setOpen = (open: boolean) => {
    toggle.setAttribute('aria-expanded', String(open))
    nav.toggleAttribute('data-open', open)
  }
  toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'))
  nav.addEventListener('click', (event) => {
    const target = event.target as Element | null
    if (target?.closest('a')) setOpen(false)
  })
}

function initLetterPortal(root: ParentNode): void {
  const portal = root.querySelector<HTMLElement>('[data-aas-letter-portal]')
  if (!portal) return
  portal.querySelectorAll<HTMLInputElement>('input[name="aas-letter"]').forEach((input) => {
    input.addEventListener('change', () => {
      if (!input.checked) return
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const labels = [...portal.querySelectorAll<HTMLElement>('label')]
      const idx = labels.findIndex((label) => label.querySelector('input') === input)
      labels.forEach((label, i) => {
        label.style.setProperty('--aas-letter-i', String(Math.abs(i - Math.max(idx, 0))))
        label.classList.toggle('is-portal-origin', i === idx)
      })
      portal.classList.remove('is-entering')
      void portal.offsetWidth
      portal.classList.add('is-entering')
      window.setTimeout(() => portal.classList.remove('is-entering'), 700)
    })
  })
}

/** Book a call follows VITE_BORG_SALES_DEMO_URL (default /borg-for-business/#contact). */
function boot(root: ParentNode = document): void {
  if (!root.querySelector('[data-lp-ai-team]')) return
  const demo = resolveSalesChatConfig().demoUrl
  root.querySelectorAll<HTMLAnchorElement>('[data-book-call]').forEach((link) => {
    link.href = demo
  })
  bindCollectiveTabs(root)
  applyRouting(root, 'sort-tickets')
  root.querySelectorAll<HTMLButtonElement>('[data-aas-task]').forEach((btn) => {
    btn.addEventListener('click', () => applyRouting(root, btn.dataset.aasTask || 'sort-tickets'))
  })
  root.querySelectorAll<HTMLButtonElement>('[data-aas-dot]').forEach((dot) => {
    dot.addEventListener('click', () => applyStory(root, Number(dot.dataset.aasDot)))
  })
  root.querySelectorAll<HTMLButtonElement>('[data-aas-size]').forEach((btn) => {
    btn.addEventListener('click', () => applySize(root, btn.dataset.aasSize || ''))
  })
  initNav(root)
  initLetterPortal(root)
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!reduced) {
    let story = 0
    const total = root.querySelectorAll('[data-aas-story]').length
    if (total) {
      window.setInterval(() => {
        story = (story + 1) % total
        applyStory(root, story)
      }, 7000)
    }
  }
}

boot()

export { bindCollectiveTabs }
